import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck,
  Check,
  X,
  ExternalLink,
  Loader2,
  RefreshCw,
  Download,
  Filter,
  Archive,
  Copy,
  Gift,
  Search,
  AtSign,
  History,
  Hourglass,
  Stethoscope,
  Users,
  Scale,
} from "lucide-react";
import {
  getBuilderGrantsArchive,
  reviewBuilderGrant,
  grantBuilderDirect,
  architectSearchUsers,
  architectBackfillQueue,
  architectListOpinions,
  architectReclassifySubmission,
  architectDismissUnclassifiable,
  architectAutoClassifyAgreed,
  architectRetryFailedTriangulations,
  architectTriangulateConsoleHistory,
  architectRetriggerStuckReconciles,
  architectSystemPayoutAudit,
  architectUsersLedger,
  architectBackfillWelcomeMints,
  architectRunWatcherNow,
} from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import PasswordResetInbox from "../components/architect/PasswordResetInbox";

function _short(s, head = 8, tail = 4) {
  if (!s) return "—";
  if (s.length <= head + tail + 1) return s;
  return `${s.slice(0, head)}…${s.slice(-tail)}`;
}

function _fmtAmount(n) {
  if (n === null || n === undefined) return "—";
  return `${Number(n).toFixed(2)} USDC`;
}

function _fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function RiskBadge({ risk }) {
  const map = {
    low:    { label: "LOW RISK",    cls: "border-emerald-500/40 text-emerald-300 bg-emerald-500/5" },
    medium: { label: "MED RISK",    cls: "border-amber-500/40 text-amber-300 bg-amber-500/5" },
    high:   { label: "HIGH RISK",   cls: "border-rose-500/40 text-rose-300 bg-rose-500/5" },
    manual: { label: "MANUAL",      cls: "border-sky-500/40 text-sky-300 bg-sky-500/5" },
  };
  const m = map[risk] || map.manual;
  return (
    <span
      data-testid={`grant-risk-badge-${risk || "unknown"}`}
      className={`px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border font-mono ${m.cls}`}
    >
      {m.label}
    </span>
  );
}

function ReviewBadge({ reviewed }) {
  return reviewed ? (
    <span
      data-testid="grant-status-reviewed"
      className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border font-mono border-emerald-500/40 text-emerald-300 bg-emerald-500/5"
    >
      <Check className="inline h-2.5 w-2.5 mr-1 -mt-0.5" />
      REVIEWED
    </span>
  ) : (
    <span
      data-testid="grant-status-pending"
      className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border font-mono border-amber-500/40 text-amber-300 bg-amber-500/5"
    >
      PENDING REVIEW
    </span>
  );
}

function GrantRow({ grant, onReviewed }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [note, setNote] = useState("");

  const copy = (txt, label) => {
    navigator.clipboard.writeText(txt).then(
      () => toast.success(`${label} copied`),
      () => toast.error("Clipboard blocked"),
    );
  };

  const downloadPng = () => {
    if (!grant.receipt_png_b64) return;
    const a = document.createElement("a");
    a.href = `data:image/png;base64,${grant.receipt_png_b64}`;
    a.download = `builder_receipt_${(grant.signature || "x").slice(0, 16)}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const markReviewed = async () => {
    if (grant.architect_reviewed) return;
    setSubmitting(true);
    try {
      await reviewBuilderGrant(grant.signature, note.trim() || null);
      toast.success("Marked reviewed · filed in documents of active builders");
      onReviewed?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark reviewed");
    } finally {
      setSubmitting(false);
    }
  };

  const verdict = grant.ai_verdict || {};
  const explorerUrl = grant.signature
    ? `https://solscan.io/tx/${grant.signature}`
    : null;

  return (
    <div
      data-testid={`grant-row-${grant.signature}`}
      className={`border ${grant.architect_reviewed ? "border-zinc-800" : "border-amber-500/30"} bg-zinc-950 hover:border-amber-500/60 transition-colors`}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`grant-toggle-${grant.signature}`}
        className="w-full flex items-center justify-between gap-3 p-3 text-left"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="font-mono text-sm text-amber-300 shrink-0">
            {_fmtAmount(grant.amount_usdc)}
          </div>
          <div className="text-xs text-zinc-400 truncate">
            <span className="text-zinc-200">{grant.user_email || grant.user_id}</span>
            <span className="text-zinc-600 mx-2">·</span>
            <span className="font-mono">{_short(grant.signature, 10, 6)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <RiskBadge risk={verdict.farming_risk} />
          <ReviewBadge reviewed={grant.architect_reviewed} />
          <span className="text-[10px] text-zinc-500 font-mono">
            {_fmtDate(grant.archived_at)}
          </span>
        </div>
      </button>

      {open && (
        <div className="border-t border-zinc-900 p-4 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr,420px] gap-4">
            {/* PNG receipt */}
            <div className="bg-black border border-zinc-900">
              {grant.receipt_png_b64 ? (
                <img
                  data-testid={`grant-receipt-${grant.signature}`}
                  src={`data:image/png;base64,${grant.receipt_png_b64}`}
                  alt={`Builder grant receipt for ${grant.signature}`}
                  className="w-full h-auto"
                />
              ) : (
                <div className="p-6 text-xs text-zinc-500 text-center">
                  No receipt PNG was archived for this grant.
                </div>
              )}
              <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-zinc-900">
                <button
                  onClick={downloadPng}
                  disabled={!grant.receipt_png_b64}
                  data-testid={`grant-download-${grant.signature}`}
                  className="flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-[0.2em] border border-zinc-800 text-zinc-300 hover:border-amber-500/40 hover:text-amber-300 disabled:opacity-30"
                >
                  <Download className="h-3 w-3" />
                  Save PNG
                </button>
                {explorerUrl && (
                  <a
                    href={explorerUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid={`grant-solscan-${grant.signature}`}
                    className="flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-[0.2em] border border-zinc-800 text-sky-300 hover:border-sky-500/40"
                  >
                    <ExternalLink className="h-3 w-3" />
                    View on Solscan
                  </a>
                )}
              </div>
            </div>

            {/* Facts panel */}
            <div className="space-y-3 text-xs">
              <Fact label="USER" value={grant.user_email || grant.user_id} mono />
              <Fact
                label="SENDER WALLET"
                value={grant.sender_wallet || "—"}
                mono
                onCopy={() => copy(grant.sender_wallet, "Sender wallet")}
              />
              <Fact
                label="DESTINATION"
                value={grant.target_wallet || "—"}
                mono
                onCopy={() => copy(grant.target_wallet, "Destination wallet")}
              />
              <Fact
                label="SIGNATURE"
                value={grant.signature || "—"}
                mono
                onCopy={() => copy(grant.signature, "Signature")}
              />
              <Fact
                label="BLOCK TIME"
                value={
                  grant.block_time
                    ? new Date(grant.block_time * 1000).toLocaleString()
                    : "—"
                }
              />
              <Fact label="EXPIRES" value={_fmtDate(grant.expires_at)} />
              <Fact
                label="GRANTED VIA"
                value={grant.auto_approved ? "Auto · AI-verified" : "Architect · manual"}
              />
              {verdict.reasoning && (
                <div>
                  <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
                    AI AUDIT
                  </div>
                  <div className="border border-zinc-900 bg-black/50 p-2 text-zinc-300 text-[11px] leading-relaxed">
                    {verdict.reasoning}
                  </div>
                  <div className="mt-1 text-[9px] text-zinc-600 font-mono">
                    source: {verdict.source || "—"} ·
                    confidence: {verdict.confidence != null ? Number(verdict.confidence).toFixed(2) : "—"}
                  </div>
                </div>
              )}

              {/* Review action */}
              {grant.architect_reviewed ? (
                <div
                  data-testid={`grant-reviewed-meta-${grant.signature}`}
                  className="border border-emerald-500/30 bg-emerald-500/5 p-2 text-emerald-300 text-[11px]"
                >
                  <div className="flex items-center gap-1.5">
                    <Check className="h-3 w-3" />
                    <span className="font-mono">Reviewed</span>
                  </div>
                  {grant.architect_note && (
                    <div className="mt-1 text-zinc-300 italic">&ldquo;{grant.architect_note}&rdquo;</div>
                  )}
                </div>
              ) : (
                <div className="pt-1 space-y-2">
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value.slice(0, 500))}
                    placeholder="Optional note · filed alongside this grant (max 500 chars)"
                    rows={2}
                    data-testid={`grant-note-${grant.signature}`}
                    className="w-full bg-black border border-zinc-800 text-zinc-200 text-[11px] p-2 font-mono focus:border-amber-500/40 outline-none"
                  />
                  <button
                    onClick={markReviewed}
                    disabled={submitting}
                    data-testid={`grant-review-btn-${grant.signature}`}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.25em] border border-amber-500/50 text-amber-300 hover:bg-amber-500/10 disabled:opacity-50"
                  >
                    {submitting ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <ShieldCheck className="h-3 w-3" />
                    )}
                    {submitting ? "Filing…" : "Mark Reviewed · file in active builders"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Fact({ label, value, mono, onCopy }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-0.5 flex items-center justify-between">
        <span>{label}</span>
        {onCopy && (
          <button
            onClick={onCopy}
            className="text-zinc-600 hover:text-amber-300"
            title="Copy"
            data-testid={`grant-copy-${label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <Copy className="h-3 w-3" />
          </button>
        )}
      </div>
      <div className={`text-zinc-200 break-all ${mono ? "font-mono text-[11px]" : ""}`}>
        {value}
      </div>
    </div>
  );
}

export default function GrantsArchive() {
  const { user, loading: authLoading } = useAuth();
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending"); // pending | all | reviewed
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getBuilderGrantsArchive(200);
      setGrants(res?.grants || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.is_architect) load();
  }, [user?.is_architect, load]);

  const visible = useMemo(() => {
    if (filter === "all") return grants;
    if (filter === "reviewed") return grants.filter((g) => g.architect_reviewed);
    return grants.filter((g) => !g.architect_reviewed);
  }, [grants, filter]);

  const counts = useMemo(() => {
    const pending = grants.filter((g) => !g.architect_reviewed).length;
    return { pending, reviewed: grants.length - pending, total: grants.length };
  }, [grants]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-black text-zinc-400 flex items-center justify-center font-mono text-xs">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (!user?.is_architect) {
    return (
      <div className="min-h-screen bg-black text-zinc-400 px-6 py-16">
        <div className="max-w-xl mx-auto border border-zinc-900 p-8 text-center">
          <Archive className="h-8 w-8 text-zinc-700 mx-auto mb-3" />
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">
            Architect Only
          </div>
          <p className="text-sm text-zinc-300">
            This is the private archive of paid Builder grants. Only the
            Architect can review the receipts and file them.
          </p>
          <Link
            to="/console"
            data-testid="grants-archive-back"
            className="inline-block mt-6 text-xs uppercase tracking-[0.25em] text-amber-300 hover:underline"
          >
            ← Back to Console
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-zinc-200">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-amber-300 mb-2">
              <Archive className="h-3 w-3" />
              Architect · Builder grants archive
            </div>
            <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-zinc-100">
              Documents of active builders.
            </h1>
            <p className="mt-2 text-sm text-zinc-400 max-w-2xl">
              Every paid Builder grant lands here with its rendered receipt and
              on-chain evidence. Mark each one reviewed once you have
              cross-checked it on Solscan and filed it in your records.
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="grants-archive-refresh"
            className="flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.25em] border border-zinc-800 text-zinc-300 hover:border-amber-500/40 hover:text-amber-300 disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {/* Stats + filter */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <StatCard
            label="Pending"
            value={counts.pending}
            tone="amber"
            active={filter === "pending"}
            onClick={() => setFilter("pending")}
            testid="grants-filter-pending"
          />
          <StatCard
            label="Reviewed"
            value={counts.reviewed}
            tone="emerald"
            active={filter === "reviewed"}
            onClick={() => setFilter("reviewed")}
            testid="grants-filter-reviewed"
          />
          <StatCard
            label="All grants"
            value={counts.total}
            tone="zinc"
            active={filter === "all"}
            onClick={() => setFilter("all")}
            testid="grants-filter-all"
          />
        </div>

        {/* Architect direct-grant comp card */}
        <DirectGrantCard onGranted={load} />

        {/* Password reset inbox — locked-out users awaiting approval */}
        <PasswordResetInbox />

        {/* Recovery Console — unified 4-step manual flow for
            resurrecting stuck / mis-triangulated submissions.
            Consolidates Retry Failed Triangulations, Backfill Queue,
            Auto-Mint Agreed, and Manual Reclassify into ONE card with
            numbered steps so the architect walks the pipeline in
            order. */}
        <RecoveryConsoleCard />

        {/* Other admin tools — unrelated to recovery flow, but useful
            for full-system audits and edge-case fixes */}
        <div className="mt-8 mb-4 flex items-center gap-3">
          <div className="h-px flex-1 bg-zinc-900" />
          <div className="text-[9px] uppercase tracking-[0.4em] text-zinc-600 font-mono">
            other admin tools
          </div>
          <div className="h-px flex-1 bg-zinc-900" />
        </div>

        {/* Console history reconcile — pull buried claims out of free
            users' Console chats and pay them out. */}
        <ConsoleReconcileCard />

        {/* Full system payout audit — walk every profile, find saved
            but unpaid submissions/citations, route each to the right
            mint path. The "did everyone get paid?" button. */}
        <SystemPayoutAuditCard />

        {/* Active users ledger — the full list of every signed-in user
            so newcomers without a @handle can never go invisible. */}
        <ActiveUsersLedgerCard />

        {/* List */}
        {error ? (
          <div
            data-testid="grants-archive-error"
            className="border border-rose-500/30 bg-rose-500/5 p-4 text-xs text-rose-300"
          >
            {error}
          </div>
        ) : loading ? (
          <div className="text-center py-16 text-xs text-zinc-500 font-mono">
            <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
            Loading grants…
          </div>
        ) : visible.length === 0 ? (
          <div
            data-testid="grants-archive-empty"
            className="border border-zinc-900 bg-zinc-950 p-10 text-center"
          >
            <Filter className="h-6 w-6 text-zinc-700 mx-auto mb-3" />
            <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">
              Nothing here
            </div>
            <p className="text-sm text-zinc-400">
              {filter === "pending"
                ? "No grants are awaiting review. You are caught up."
                : filter === "reviewed"
                ? "No grants have been reviewed yet."
                : "No Builder grants have been archived yet."}
            </p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="grants-archive-list">
            {visible.map((g) => (
              <GrantRow key={g.signature} grant={g} onReviewed={load} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DirectGrantCard({ onGranted }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastGrant, setLastGrant] = useState(null);

  // Debounced search — fires 280ms after typing stops.
  useEffect(() => {
    if (picked) return;
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await architectSearchUsers(q, 12);
        if (!cancelled) {
          setResults(r.items || []);
          setOpen(true);
        }
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 280);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, picked]);

  const pick = (u) => {
    setPicked(u);
    setQuery("");
    setResults([]);
    setOpen(false);
  };

  const clearPick = () => {
    setPicked(null);
    setLastGrant(null);
  };

  const submit = async () => {
    if (!picked) {
      toast.error("Pick a user first");
      return;
    }
    setBusy(true);
    try {
      const payload = { user_id: picked.user_id, note: note.trim() || null };
      const r = await grantBuilderDirect(payload);
      setLastGrant(r);
      const who = r.username ? `@${r.username}` : r.name || r.email;
      toast.success(`Granted Builder to ${who} until ${r.expires_at?.slice(0, 10)}`);
      setPicked(null);
      setNote("");
      onGranted && onGranted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grant failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-4 border border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-transparent p-5" data-testid="direct-grant-card">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-amber-300 mb-2">
        <Gift className="h-3 w-3" /> Direct grant · comp 30 days Builder
      </div>
      <p className="text-xs text-zinc-400 mb-3 leading-relaxed">
        Find a user by display name, @handle, or email — then comp them
        30 days of Builder. Works even if they never claimed a @username.
      </p>

      {!picked && (
        <div className="relative mb-2" data-testid="direct-grant-search-wrap">
          <div className="flex items-center gap-2 bg-black border border-zinc-800 px-3 focus-within:border-amber-500/50">
            <Search className="h-3.5 w-3.5 text-zinc-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="search by name, handle, or email…"
              data-testid="direct-grant-search"
              className="flex-1 bg-transparent py-2 text-sm text-zinc-100 focus:outline-none"
              autoComplete="off"
            />
            {searching && <Loader2 className="h-3.5 w-3.5 text-zinc-500 animate-spin" />}
          </div>
          {open && results.length > 0 && (
            <div className="absolute left-0 right-0 mt-1 bg-zinc-950 border border-zinc-800 max-h-80 overflow-auto z-30 shadow-xl" data-testid="direct-grant-results">
              {results.map((u) => (
                <button
                  key={u.user_id}
                  onClick={() => pick(u)}
                  data-testid={`direct-grant-result-${u.user_id}`}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-zinc-900 border-b border-zinc-900 last:border-b-0"
                >
                  {u.picture ? (
                    <img src={u.picture} alt="" className="h-7 w-7 rounded-full bg-zinc-900 shrink-0" />
                  ) : (
                    <div className="h-7 w-7 rounded-full bg-zinc-900 grid place-items-center text-[10px] text-zinc-500 shrink-0">
                      {(u.name || u.email || "?").slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-sm text-zinc-100 truncate">
                      <span className="truncate">{u.name || u.email_masked || u.user_id}</span>
                      {u.is_architect && (
                        <span className="text-[9px] uppercase tracking-[0.2em] text-amber-300 bg-amber-500/10 border border-amber-500/30 px-1 py-0.5">
                          architect
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-zinc-500 flex items-center gap-2 truncate font-mono">
                      {u.username ? (
                        <span className="text-amber-300/80">@{u.username}</span>
                      ) : (
                        <span className="text-zinc-600 italic">no @handle</span>
                      )}
                      <span>·</span>
                      <span className="truncate">{u.email_masked}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
          {open && results.length === 0 && !searching && query.trim().length >= 2 && (
            <div className="absolute left-0 right-0 mt-1 bg-zinc-950 border border-zinc-800 px-3 py-3 text-xs text-zinc-500 z-30">
              No matches for &quot;{query}&quot;
            </div>
          )}
        </div>
      )}

      {picked && (
        <div className="border border-amber-500/50 bg-zinc-950 p-3 mb-3" data-testid="direct-grant-picked">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              {picked.picture ? (
                <img src={picked.picture} alt="" className="h-9 w-9 rounded-full bg-zinc-900 shrink-0" />
              ) : (
                <div className="h-9 w-9 rounded-full bg-zinc-900 grid place-items-center text-zinc-500 shrink-0">
                  {(picked.name || picked.email || "?").slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <div className="text-sm text-zinc-100 truncate">{picked.name || picked.email}</div>
                <div className="text-[11px] text-zinc-500 font-mono flex items-center gap-2 truncate">
                  {picked.username ? (
                    <span className="text-amber-300/80">@{picked.username}</span>
                  ) : (
                    <span className="text-zinc-600 italic inline-flex items-center gap-1"><AtSign className="h-3 w-3" />no handle</span>
                  )}
                  <span>·</span>
                  <span className="truncate">{picked.email_masked}</span>
                </div>
              </div>
            </div>
            <button
              onClick={clearPick}
              data-testid="direct-grant-clear"
              className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-amber-300"
            >
              change
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-[2] min-w-[260px]">
          <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-1">Note (optional)</label>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="why this comp grant?"
            data-testid="direct-grant-note"
            className="w-full bg-black border border-zinc-800 px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-amber-500/50"
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          />
        </div>
        <button
          onClick={submit}
          disabled={busy || !picked}
          data-testid="direct-grant-submit"
          className="px-4 py-2 border border-amber-500/60 text-amber-300 hover:bg-amber-500 hover:text-zinc-950 text-[11px] uppercase tracking-[0.25em] disabled:opacity-40"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1.5" /> : <Gift className="h-3.5 w-3.5 inline mr-1.5" />}
          {busy ? "Granting…" : "Grant 30 days"}
        </button>
      </div>

      {lastGrant && (
        <div className="mt-3 border border-emerald-500/40 bg-emerald-500/5 p-2 text-xs text-emerald-200" data-testid="direct-grant-success">
          <ShieldCheck className="h-3 w-3 inline mr-1.5" />
          Granted Builder to{" "}
          <span className="font-mono text-amber-300">
            {lastGrant.username ? `@${lastGrant.username}` : lastGrant.name || lastGrant.email}
          </span>{" "}
          until <span className="font-mono">{lastGrant.expires_at?.slice(0, 10)}</span>
        </div>
      )}
    </div>
  );
}


function StatCard({ label, value, tone, active, onClick, testid }) {
  const tones = {
    amber:   { border: "border-amber-500/30",   text: "text-amber-300" },
    emerald: { border: "border-emerald-500/30", text: "text-emerald-300" },
    zinc:    { border: "border-zinc-800",       text: "text-zinc-300" },
  };
  const t = tones[tone] || tones.zinc;
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-left p-3 border bg-zinc-950 transition-colors ${
        active ? `${t.border} bg-zinc-900` : "border-zinc-900 hover:border-zinc-700"
      }`}
    >
      <div className={`text-[9px] uppercase tracking-[0.25em] ${t.text}`}>
        {label}
      </div>
      <div className="text-2xl font-light text-zinc-100 mt-1">{value}</div>
    </button>
  );
}


function BackfillQueueCard() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      const res = await architectBackfillQueue();
      setResult(res);
      toast.success(
        `Backfill complete: ${res.minted || 0} minted · ${res.marked_bounty || 0} bounties · ${res.skipped_opinion || 0} opinions skipped.`
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Backfill failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="border border-amber-500/30 bg-amber-500/5 p-5"
      data-testid="backfill-queue-card"
    >
      <div className="flex items-start gap-3">
        <Hourglass className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300">
            Stuck queue cleanup
          </div>
          <div className="font-heading text-lg text-zinc-100 mt-1">
            Backfill old submissions
          </div>
          <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
            Retroactively run every triangulated submission that&apos;s stuck in
            the queue through the current auto-classifier. Consensus mints in
            the submitter&apos;s bucket; consensus that opposed the submitter mints
            in the <span className="text-amber-300">flipped</span> bucket
            (they still earn AEGIS · claim stays open to dispute). Only truly
            uncertain claims stay in <span className="font-mono text-zinc-300">/truths</span> as
            bounties.
          </p>
        </div>
        <button
          type="button"
          onClick={run}
          disabled={busy}
          data-testid="backfill-queue-run-btn"
          className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 border border-amber-500 text-amber-300 hover:bg-amber-500 hover:text-zinc-950 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-[10px] uppercase tracking-[0.3em]"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          {busy ? "Running…" : "Run backfill"}
        </button>
      </div>
      {result && (
        <div
          data-testid="backfill-queue-result"
          className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]"
        >
          <div className="border border-zinc-800 bg-zinc-950 p-2">
            <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">Scanned</div>
            <div className="text-zinc-100 mt-1 font-mono">{result.scanned ?? 0}</div>
          </div>
          <div className="border border-emerald-500/30 bg-emerald-500/5 p-2">
            <div className="text-[9px] uppercase tracking-[0.25em] text-emerald-300">Minted</div>
            <div className="text-emerald-200 mt-1 font-mono">{result.minted ?? 0}</div>
          </div>
          <div className="border border-amber-500/30 bg-amber-500/5 p-2">
            <div className="text-[9px] uppercase tracking-[0.25em] text-amber-300">Bounties</div>
            <div className="text-amber-200 mt-1 font-mono">{result.marked_bounty ?? 0}</div>
          </div>
          <div className="border border-zinc-800 bg-zinc-950 p-2">
            <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">Skipped</div>
            <div className="text-zinc-100 mt-1 font-mono">{result.skipped_opinion ?? 0}</div>
          </div>
        </div>
      )}
    </section>
  );
}

function RetryFailedTriangulationsCard() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      const res = await architectRetryFailedTriangulations(200);
      setResult(res);
      if (res.queued > 0) {
        toast.success(
          `Queued ${res.queued} re-triangulation${res.queued === 1 ? "" : "s"}. Check /queue in ~30s.`
        );
      } else {
        toast.info(res.message || "No failed triangulations to retry.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="border border-rose-500/30 bg-rose-500/5 p-5"
      data-testid="retry-failed-triangulations-card"
    >
      <div className="flex items-start gap-3">
        <RefreshCw className="h-4 w-4 text-rose-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-rose-300">
            Recovery
          </div>
          <div className="font-heading text-lg text-zinc-100 mt-1">
            Retry failed triangulations
          </div>
          <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
            Finds submissions that were wrongly closed as opinion because both
            LLMs errored out mid-flight (typical cause: universal LLM key
            budget exhausted). Resets each row to pending and re-fires
            triangulation. <span className="text-rose-300">Requires the LLM budget to be topped up first</span> — otherwise they&apos;ll just crash again.
          </p>
        </div>
        <button
          type="button"
          onClick={run}
          disabled={busy}
          data-testid="retry-failed-triangulations-btn"
          className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 border border-rose-500 text-rose-300 hover:bg-rose-500 hover:text-zinc-950 disabled:opacity-50 disabled:cursor-not-allowed text-[10px] uppercase tracking-[0.3em]"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          {busy ? "Running…" : "Retry now"}
        </button>
      </div>
      {result && (
        <div
          data-testid="retry-failed-triangulations-result"
          className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11px]"
        >
          <div className="border border-zinc-800 bg-zinc-950 p-2">
            <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">Scanned</div>
            <div className="text-zinc-100 mt-1 font-mono">{result.scanned ?? 0}</div>
          </div>
          <div className="border border-emerald-500/30 bg-emerald-500/5 p-2">
            <div className="text-[9px] uppercase tracking-[0.25em] text-emerald-300">Queued</div>
            <div className="text-emerald-200 mt-1 font-mono">{result.queued ?? 0}</div>
          </div>
          <div className="border border-rose-500/30 bg-rose-500/5 p-2">
            <div className="text-[9px] uppercase tracking-[0.25em] text-rose-300">Errors</div>
            <div className="text-rose-200 mt-1 font-mono">{result.errors ?? 0}</div>
          </div>
        </div>
      )}
    </section>
  );
}


function ReclassifyOpinionsCard() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [autoBusy, setAutoBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await architectListOpinions(200);
      setRows(res.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load opinions.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const autoMintAgreed = async () => {
    setAutoBusy(true);
    try {
      const res = await architectAutoClassifyAgreed(200);
      const total = (res.minted_truth || 0) + (res.minted_lie || 0);
      if (total > 0) {
        toast.success(
          `Auto-minted ${total} · truth ${res.minted_truth} · lie ${res.minted_lie} · skipped ${res.skipped}`
        );
      } else {
        toast.info(`Nothing to auto-mint (skipped ${res.skipped}).`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-mint failed.");
    } finally {
      setAutoBusy(false);
    }
  };

  const reclassify = async (id, newType) => {
    setBusyId(id);
    try {
      await architectReclassifySubmission(id, newType);
      toast.success(
        `Minted as ${newType} — AEGIS credited to submitter (51/5/39/5).`
      );
      setRows((prev) => (prev || []).filter((r) => r.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reclassify failed.");
    } finally {
      setBusyId(null);
    }
  };

  const dismiss = async (id) => {
    setBusyId(id);
    try {
      await architectDismissUnclassifiable(id);
      toast.success("Confirmed unclassifiable — removed from queue.");
      setRows((prev) => (prev || []).filter((r) => r.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dismiss failed.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section
      className="border border-sky-500/30 bg-sky-500/5 p-5"
      data-testid="reclassify-opinions-card"
    >
      <div className="flex items-start gap-3 mb-4">
        <Scale className="h-4 w-4 text-sky-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-sky-300">
            Historical cleanup · one-time recovery
          </div>
          <div className="font-heading text-lg text-zinc-100 mt-1">
            Rescue stuck opinions
          </div>
          <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
            One-shot tool to reclassify submissions parked as opinion during
            the LLM-budget crash. New submissions from here on flow through the
            standard triangulation pipeline and this tool won&apos;t be needed
            again. Hit <span className="text-emerald-300">Auto-mint agreed</span>{" "}
            first to sweep everything where the LLMs already agreed — then walk
            any leftovers manually.
          </p>
        </div>
        <div className="shrink-0 flex flex-col gap-2">
          <button
            type="button"
            onClick={autoMintAgreed}
            disabled={autoBusy || loading}
            data-testid="reclassify-auto-mint"
            className="inline-flex items-center gap-1 px-3 py-2 border border-emerald-500 text-emerald-300 hover:bg-emerald-500 hover:text-zinc-950 disabled:opacity-50 text-[10px] uppercase tracking-[0.3em] whitespace-nowrap"
            title="Auto-mint every row where Claude+Gemini already agreed on true or false — no manual clicks needed"
          >
            {autoBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
            {autoBusy ? "Minting…" : "Auto-mint agreed"}
          </button>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="reclassify-reload"
            className="inline-flex items-center gap-1 px-3 py-2 border border-sky-500/60 text-sky-300 hover:bg-sky-500/15 disabled:opacity-50 text-[10px] uppercase tracking-[0.3em]"
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            {loading ? "Loading" : "Reload"}
          </button>
        </div>
      </div>

      {!loading && rows && rows.length === 0 && (
        <div className="text-[11px] text-zinc-500 italic py-6 text-center font-mono uppercase tracking-[0.25em]" data-testid="reclassify-empty">
          Nothing to reclassify — clean slate.
        </div>
      )}

      {rows && rows.length > 0 && (
        <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1" data-testid="reclassify-list">
          {rows.map((r) => {
            const cv = r.triangulation?.claude?.verdict;
            const gv = r.triangulation?.gemini?.verdict;
            const cr = r.triangulation?.claude?.reasoning || "";
            const gr = r.triangulation?.gemini?.reasoning || "";
            const busy = busyId === r.id;
            return (
              <div
                key={r.id}
                className="border border-zinc-800 bg-black/40 p-3"
                data-testid={`reclassify-row-${r.id}`}
              >
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-1 flex items-center justify-between">
                  <span>{r.status} · claimed as {r.claim_type || "—"}</span>
                  <span>{r.user_email || r.submitter_email || "—"}</span>
                </div>
                <div className="text-sm text-zinc-100 leading-relaxed">
                  &ldquo;{r.claim}&rdquo;
                </div>
                <div className="mt-2 text-[10px] font-mono text-zinc-500 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  <div>
                    <span className="text-amber-400">Claude</span> · {cv || "—"} · <span className="text-zinc-400">{cr.slice(0, 220)}</span>
                  </div>
                  <div>
                    <span className="text-sky-400">Gemini</span> · {gv || "—"} · <span className="text-zinc-400">{gr.slice(0, 220)}</span>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => reclassify(r.id, "truth")}
                    data-testid={`reclassify-truth-${r.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-emerald-500 text-emerald-300 hover:bg-emerald-500 hover:text-zinc-950 disabled:opacity-50 text-[10px] uppercase tracking-[0.3em]"
                  >
                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                    Mint as truth
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => reclassify(r.id, "lie")}
                    data-testid={`reclassify-lie-${r.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-rose-500 text-rose-300 hover:bg-rose-500 hover:text-zinc-950 disabled:opacity-50 text-[10px] uppercase tracking-[0.3em]"
                  >
                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                    Mint as lie
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => dismiss(r.id)}
                    data-testid={`reclassify-dismiss-${r.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-600 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200 disabled:opacity-50 text-[10px] uppercase tracking-[0.3em]"
                    title="Not a factual claim — confirm as unclassifiable and remove from queue (no mint)"
                  >
                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Archive className="h-3 w-3" />}
                    Unclassifiable
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// Numbered step tile inside RecoveryConsoleCard. Lifted to module
// scope so React doesn't rebuild it on every parent render.
function RecoveryStepShell({ n, tone, title, hint, resultKey, onRun, disabled, running, results }) {
  const res = results[resultKey];
  return (
    <div className={`border ${tone.border} ${tone.bg} p-4`} data-testid={`recovery-step-${n}`}>
      <div className="flex items-start gap-3">
        <div className={`shrink-0 h-8 w-8 border ${tone.border} ${tone.text} flex items-center justify-center font-mono text-sm`}>{n}</div>
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] uppercase tracking-[0.3em] ${tone.text}`}>Step {n}</div>
          <div className="font-heading text-base text-zinc-100 mt-0.5">{title}</div>
          <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed">{hint}</p>
          {res && (
            <div className="mt-2 text-[10px] font-mono text-zinc-500 leading-tight">
              last run: {JSON.stringify(res).slice(0, 200)}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onRun}
          disabled={disabled || running}
          data-testid={`recovery-step-${n}-btn`}
          className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 border ${tone.border} ${tone.text} disabled:opacity-40 disabled:cursor-not-allowed text-[10px] uppercase tracking-[0.25em] whitespace-nowrap`}
        >
          {running ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          {running ? "Running…" : "Run"}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
//  RECOVERY CONSOLE — unified 4-step manual flow.
// ─────────────────────────────────────────────────────────────────
//  Consolidates Retry Failed Triangulations, Backfill Queue,
//  Auto-Mint Agreed, and Manual Reclassify into one card with
//  numbered steps + running counts. Walks the architect through
//  the recovery pipeline in the correct order.
// ─────────────────────────────────────────────────────────────────
function RecoveryConsoleCard() {
  const [pending, setPending] = useState(null);
  const [busyStep, setBusyStep] = useState(null);
  const [results, setResults] = useState({});
  const [manualBusyId, setManualBusyId] = useState(null);

  const loadPending = useCallback(async () => {
    setPending((prev) => ({ ...(prev || {}), loading: true }));
    try {
      const res = await architectListOpinions(200);
      setPending({ list: res.rows || [], loading: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load pending queue.");
      setPending((prev) => ({ ...(prev || { list: [] }), loading: false }));
    }
  }, []);

  useEffect(() => { loadPending(); }, [loadPending]);

  const runStep1 = async () => {
    setBusyStep(1);
    try {
      const res = await architectRetryFailedTriangulations(200);
      setResults((r) => ({ ...r, step1: res }));
      toast.success(`Step 1 · queued ${res.queued} re-triangulations (wait ~30s then Step 2).`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Step 1 failed."); }
    finally { setBusyStep(null); }
  };

  const runStep0 = async () => {
    setBusyStep(0);
    try {
      const res = await architectRunWatcherNow();
      setResults((r) => ({ ...r, step0: res }));
      toast.success(
        `Watcher · scanned ${res.scanned} · retried ${res.queued_for_retry} · parked ${res.parked_as_opinion} · cutoff-hard ${res.cutoff_hard}`
      );
      await loadPending();
    } catch (e) { toast.error(e?.response?.data?.detail || "Watcher failed."); }
    finally { setBusyStep(null); }
  };

  const runStep2 = async () => {
    setBusyStep(2);
    try {
      const res = await architectBackfillQueue();
      setResults((r) => ({ ...r, step2: res }));
      toast.success(`Step 2 · minted ${res.minted} · bountied ${res.marked_bounty}`);
      await loadPending();
    } catch (e) { toast.error(e?.response?.data?.detail || "Step 2 failed."); }
    finally { setBusyStep(null); }
  };

  const runStep3 = async () => {
    setBusyStep(3);
    try {
      const res = await architectAutoClassifyAgreed(200);
      setResults((r) => ({ ...r, step3: res }));
      const total = (res.minted_truth || 0) + (res.minted_lie || 0);
      toast.success(`Step 3 · minted ${total} (truth ${res.minted_truth} · lie ${res.minted_lie})`);
      await loadPending();
    } catch (e) { toast.error(e?.response?.data?.detail || "Step 3 failed."); }
    finally { setBusyStep(null); }
  };

  const manualMint = async (id, newType) => {
    setManualBusyId(id);
    try {
      await architectReclassifySubmission(id, newType);
      toast.success(`Minted as ${newType} — AEGIS credited.`);
      setPending((p) => ({ ...(p || {}), list: (p?.list || []).filter((r) => r.id !== id) }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Reclassify failed."); }
    finally { setManualBusyId(null); }
  };

  const manualDismiss = async (id) => {
    setManualBusyId(id);
    try {
      await architectDismissUnclassifiable(id);
      toast.success("Confirmed unclassifiable — removed from queue.");
      setPending((p) => ({ ...(p || {}), list: (p?.list || []).filter((r) => r.id !== id) }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Dismiss failed."); }
    finally { setManualBusyId(null); }
  };

  const pendingCount = pending?.list?.length ?? 0;

  return (
    <section className="border border-emerald-500/30 bg-emerald-500/[0.03] p-5" data-testid="recovery-console-card">
      <div className="flex items-start gap-3 mb-4">
        <ShieldCheck className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300">Recovery Console · one-time cleanup</div>
          <div className="font-heading text-xl text-zinc-100 mt-1">Rescue stuck submissions</div>
          <p className="text-xs text-zinc-400 mt-2 leading-relaxed max-w-2xl">
            Walk the numbered steps 1 → 4 to resurrect submissions parked as opinion or bounty during LLM-budget crashes. Each step handles one class of problem. Confirm the Emergent LLM key is topped up first.
          </p>
        </div>
        <div className="shrink-0 border border-zinc-800 bg-zinc-950 p-2 text-right">
          <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">Pending</div>
          <div className="font-mono text-2xl text-emerald-300 tabular-nums">{pendingCount}</div>
        </div>
      </div>

      <div className="space-y-2.5" data-testid="recovery-steps">
        <RecoveryStepShell
          n={0}
          title="Run watcher classifier · files stuck rows"
          hint="v19.40 — scans all opinion/unresolved/failed rows, files genuine opinions into parked_as_opinion (removes from Truths feed), and re-fires triangulation on anything with error markers or where grounding never ran. This ALSO runs on boot + every 60 min automatically."
          tone={{ border: "border-sky-500/50", bg: "bg-sky-500/5", text: "text-sky-300" }}
          resultKey="step0"
          onRun={runStep0}
          running={busyStep === 0}
          disabled={busyStep !== null}
          results={results}
        />
        <RecoveryStepShell
          n={1}
          title="Retry failed triangulations"
          hint="Manual re-fire of Claude + Gemini on rows with error markers OR where grounding never ran (v19.40 broadened). Uses the web-augmented retry (v20.10) so post-cutoff claims resolve. Prefer Step 0 — it does this plus classification in one pass."
          tone={{ border: "border-rose-500/50", bg: "bg-rose-500/5", text: "text-rose-300" }}
          resultKey="step1"
          onRun={runStep1}
          running={busyStep === 1}
          disabled={busyStep !== null}
          results={results}
        />
        <RecoveryStepShell
          n={2}
          title="Backfill queue"
          hint="Applies the current auto-classifier retroactively to triangulated-but-unminted submissions. Confirmed truths mint; label mismatches flip to the opposite bucket."
          tone={{ border: "border-amber-500/50", bg: "bg-amber-500/5", text: "text-amber-300" }}
          resultKey="step2"
          onRun={runStep2}
          running={busyStep === 2}
          disabled={busyStep !== null}
          results={results}
        />
        <RecoveryStepShell
          n={3}
          title="Auto-mint LLM-agreed"
          hint="For remaining pending items where Claude + Gemini agreed on true or false, auto-mint at 51/5/39/5 into the matching bucket. Flips claim_type if needed."
          tone={{ border: "border-emerald-500/50", bg: "bg-emerald-500/5", text: "text-emerald-300" }}
          resultKey="step3"
          onRun={runStep3}
          running={busyStep === 3}
          disabled={busyStep !== null}
          results={results}
        />

        <div className="border border-sky-500/40 bg-sky-500/5 p-4" data-testid="recovery-step-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 h-8 w-8 border border-sky-500/50 text-sky-300 flex items-center justify-center font-mono text-sm">4</div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-sky-300">Step 4</div>
              <div className="font-heading text-base text-zinc-100 mt-0.5">Manual walk</div>
              <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed">
                For anything still pending, decide each by hand: mint as truth, mint as lie, or confirm unclassifiable.
              </p>
            </div>
            <button
              type="button"
              onClick={loadPending}
              disabled={pending?.loading}
              data-testid="recovery-step-4-reload"
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 border border-sky-500/50 text-sky-300 hover:bg-sky-500/15 disabled:opacity-40 text-[10px] uppercase tracking-[0.25em]"
            >
              {pending?.loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              Reload
            </button>
          </div>

          {pendingCount === 0 && !pending?.loading && (
            <div className="mt-3 text-[11px] text-emerald-400 font-mono uppercase tracking-[0.25em] text-center py-4 border border-zinc-900">
              ✓ queue empty — recovery complete
            </div>
          )}

          {pendingCount > 0 && (
            <div className="mt-3 space-y-2 max-h-[400px] overflow-y-auto pr-1" data-testid="recovery-manual-list">
              {(pending.list || []).map((r) => {
                const cv = r.triangulation?.claude?.verdict;
                const gv = r.triangulation?.gemini?.verdict;
                const busy = manualBusyId === r.id;
                return (
                  <div key={r.id} className="border border-zinc-800 bg-black/40 p-3" data-testid={`recovery-manual-row-${r.id}`}>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-1 flex items-center justify-between">
                      <span>{r.status} · claimed {r.claim_type || "—"}</span>
                      <span>{r.user_email || r.submitter_email || "—"}</span>
                    </div>
                    <div className="text-sm text-zinc-100 leading-relaxed">&ldquo;{r.claim}&rdquo;</div>
                    <div className="mt-1 text-[10px] font-mono text-zinc-500">
                      <span className="text-amber-400">Claude</span>: {cv || "—"} · <span className="text-sky-400">Gemini</span>: {gv || "—"}
                    </div>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <button type="button" disabled={busy} onClick={() => manualMint(r.id, "truth")} data-testid={`recovery-manual-truth-${r.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 border border-emerald-500 text-emerald-300 hover:bg-emerald-500 hover:text-zinc-950 disabled:opacity-50 text-[10px] uppercase tracking-[0.25em]">
                        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                        Truth
                      </button>
                      <button type="button" disabled={busy} onClick={() => manualMint(r.id, "lie")} data-testid={`recovery-manual-lie-${r.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 border border-rose-500 text-rose-300 hover:bg-rose-500 hover:text-zinc-950 disabled:opacity-50 text-[10px] uppercase tracking-[0.25em]">
                        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                        Lie
                      </button>
                      <button type="button" disabled={busy} onClick={() => manualDismiss(r.id)} data-testid={`recovery-manual-dismiss-${r.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 border border-zinc-600 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200 disabled:opacity-50 text-[10px] uppercase tracking-[0.25em]">
                        <Archive className="h-3 w-3" />
                        Unclassifiable
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}




function ConsoleReconcileCard() {
  const [hours, setHours] = useState(6);
  const [limit, setLimit] = useState(50);
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [retriggering, setRetriggering] = useState(false);
  const [preview, setPreview] = useState(null);
  const [committed, setCommitted] = useState(null);
  const [retriggered, setRetriggered] = useState(null);

  const dryRun = async () => {
    setBusy(true);
    setCommitted(null);
    try {
      const res = await architectTriangulateConsoleHistory({
        hours: Number(hours) || 6,
        commit: false,
        limit: Number(limit) || 50,
      });
      setPreview(res);
      const wc = res.would_create ?? 0;
      toast.success(
        `Found ${wc} claim${wc === 1 ? "" : "s"} buried in Console history (${res.scanned} messages scanned).`
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Console scan failed");
    } finally {
      setBusy(false);
    }
  };

  const commitRun = async () => {
    if (!preview || (preview.would_create ?? 0) === 0) {
      toast.info("Nothing to commit. Run a dry-run first.");
      return;
    }
    setCommitting(true);
    try {
      const res = await architectTriangulateConsoleHistory({
        hours: Number(hours) || 6,
        commit: true,
        limit: Number(limit) || 50,
      });
      setCommitted(res);
      setPreview(null);
      toast.success(
        `Submitted ${res.created || 0} claim${res.created === 1 ? "" : "s"} for triangulation. AEGIS pays out as each one resolves.`
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Console reconcile failed");
    } finally {
      setCommitting(false);
    }
  };

  const retriggerStuck = async () => {
    setRetriggering(true);
    try {
      const res = await architectRetriggerStuckReconciles();
      setRetriggered(res);
      if ((res.found ?? 0) === 0) {
        toast.info("No stuck reconcile submissions found.");
      } else {
        toast.success(
          `Re-running ${res.retriggered}/${res.found} stuck submission${res.retriggered === 1 ? "" : "s"}. Refresh in ~60s to see them resolve.`
        );
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Retrigger failed");
    } finally {
      setRetriggering(false);
    }
  };

  const claimCandidates = (preview?.candidates || []).filter((c) => c.is_claim);

  return (
    <section
      className="border border-emerald-500/30 bg-emerald-500/5 p-5"
      data-testid="console-reconcile-card"
    >
      <div className="flex items-start gap-3">
        <History className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300">
            Console history reconcile
          </div>
          <div className="font-heading text-lg text-zinc-100 mt-1">
            Pay out claims buried in old chats
          </div>
          <p className="text-xs text-zinc-400 mt-2 leading-relaxed max-w-2xl">
            Before v18.99, free users could chat with the Sentinel directly and
            bury real factual claims inside Console messages. This pulls the
            last N hours of those messages, asks Gemini which ones are actual
            verifiable claims, then pushes them through the standard
            triangulation pipeline. AEGIS is credited to the original sender.
            Dry-run first.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
        <label className="block">
          <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
            Hours back
          </div>
          <input
            type="number"
            min={1}
            max={720}
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            data-testid="console-reconcile-hours-input"
            className="w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500/60 outline-none text-zinc-100 px-3 py-2 font-mono text-sm"
          />
        </label>
        <label className="block">
          <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
            Max messages
          </div>
          <input
            type="number"
            min={1}
            max={200}
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            data-testid="console-reconcile-limit-input"
            className="w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500/60 outline-none text-zinc-100 px-3 py-2 font-mono text-sm"
          />
        </label>
        <div className="flex items-end gap-2">
          <button
            type="button"
            onClick={dryRun}
            disabled={busy || committing}
            data-testid="console-reconcile-dryrun-btn"
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-zinc-700 text-zinc-200 hover:border-emerald-500 hover:text-emerald-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-[10px] uppercase tracking-[0.3em]"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
            {busy ? "Scanning…" : "Dry-run"}
          </button>
        </div>
      </div>

      {/* Rescue button — unsticks rows left "triangulating" by a crashed
          background task (the v18.99.1 bug). Safe to spam; idempotent. */}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={retriggerStuck}
          disabled={retriggering}
          data-testid="console-reconcile-retrigger-btn"
          className="inline-flex items-center gap-2 px-4 py-2 border border-amber-500/60 text-amber-300 hover:bg-amber-500 hover:text-zinc-950 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-[10px] uppercase tracking-[0.3em]"
        >
          {retriggering ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          {retriggering ? "Re-running…" : "Retrigger stuck"}
        </button>
        <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-600">
          Unsticks any old &ldquo;triangulating&rdquo; reconciles
        </span>
        {retriggered && (
          <span
            data-testid="console-reconcile-retrigger-result"
            className="text-[10px] uppercase tracking-[0.3em] text-emerald-300"
          >
            · {retriggered.retriggered ?? 0} re-run · {retriggered.errors ?? 0} errored
          </span>
        )}
      </div>

      {preview && (
        <div
          data-testid="console-reconcile-preview"
          className="mt-4 border border-zinc-800 bg-zinc-950 p-4"
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
            <div className="border border-zinc-800 p-2">
              <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">Scanned</div>
              <div className="text-zinc-100 mt-1 font-mono">{preview.scanned ?? 0}</div>
            </div>
            <div className="border border-zinc-800 p-2">
              <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">Eligible users</div>
              <div className="text-zinc-100 mt-1 font-mono">{preview.eligible ?? 0}</div>
            </div>
            <div className="border border-emerald-500/30 p-2">
              <div className="text-[9px] uppercase tracking-[0.25em] text-emerald-300">Will triangulate</div>
              <div className="text-emerald-200 mt-1 font-mono">{preview.would_create ?? 0}</div>
            </div>
            <div className="border border-zinc-800 p-2">
              <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">Non-claims / dupes</div>
              <div className="text-zinc-100 mt-1 font-mono">
                {(preview.skipped_non_claim ?? 0) + (preview.skipped_existing ?? 0)}
              </div>
            </div>
          </div>

          {claimCandidates.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
                Claims to triangulate
              </div>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {claimCandidates.map((c) => (
                  <div
                    key={c.log_id}
                    data-testid={`console-reconcile-claim-${c.log_id}`}
                    className="border border-emerald-500/20 bg-emerald-500/5 p-3"
                  >
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-emerald-300">
                      <AtSign className="h-3 w-3" />
                      <span className="font-mono">{c.handle || c.email || c.user_id?.slice(0, 12)}</span>
                      <span className="text-zinc-600">·</span>
                      <span className="text-zinc-500 normal-case tracking-normal">
                        {c.timestamp ? new Date(c.timestamp).toLocaleString() : ""}
                      </span>
                    </div>
                    <div className="text-xs text-zinc-200 mt-2 leading-relaxed">
                      {c.message}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {claimCandidates.length === 0 ? (
            <p className="mt-3 text-[11px] text-zinc-500">
              No factual claims surfaced in this window. Try widening the hours.
            </p>
          ) : (
            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                onClick={commitRun}
                disabled={committing}
                data-testid="console-reconcile-commit-btn"
                className="inline-flex items-center gap-2 px-5 py-3 border border-emerald-500 text-emerald-300 hover:bg-emerald-500 hover:text-zinc-950 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-[10px] uppercase tracking-[0.3em]"
              >
                {committing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                {committing ? "Submitting…" : `Triangulate & pay out ${claimCandidates.length}`}
              </button>
              <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-600">
                AEGIS credited to each original sender
              </span>
            </div>
          )}
        </div>
      )}

      {committed && (
        <div
          data-testid="console-reconcile-committed"
          className="mt-4 border border-emerald-500/40 bg-emerald-500/10 p-4"
        >
          <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300">
            Submitted for triangulation
          </div>
          <div className="text-xs text-zinc-200 mt-2 leading-relaxed">
            {committed.created || 0} claim{committed.created === 1 ? "" : "s"} pushed into
            the pipeline. AEGIS mints land on each submitter&apos;s wallet as their
            claim resolves. {committed.errors > 0 && (
              <span className="text-rose-300"> · {committed.errors} errored</span>
            )}
          </div>
        </div>
      )}
    </section>
  );
}



function SystemPayoutAuditCard() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (busy) return;
    setBusy(true);
    toast.info("Scanning every profile for saved-but-unpaid submissions…");
    try {
      const res = await architectSystemPayoutAudit();
      setResult(res);
      const apt = (res?.summary?.newly_minted_apt || 0).toFixed(2);
      const users = (res?.per_user || []).length;
      if (parseFloat(apt) === 0 && users === 0) {
        toast.success("All caught up — every profile is paid.");
      } else {
        toast.success(
          `Audit complete · +${apt} APT minted to ${users} user${users === 1 ? "" : "s"}`
        );
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "System audit failed");
    } finally {
      setBusy(false);
    }
  };

  const s = result?.summary;

  return (
    <section
      className="border border-sky-500/30 bg-sky-500/5 p-5"
      data-testid="system-payout-audit-card"
    >
      <div className="flex items-start gap-3">
        <Stethoscope className="h-4 w-4 text-sky-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-sky-300">
            System check
          </div>
          <div className="font-heading text-lg text-zinc-100 mt-1">
            Did everyone get paid?
          </div>
          <p className="text-xs text-zinc-400 mt-2 leading-relaxed max-w-2xl">
            Walks every profile in the database. Finds submissions that
            are saved but never minted (stuck triangulating, verified
            but no coins, confirmed citations missing their mint).
            Routes each one through the right pipeline so AEGIS lands
            on the original sender&apos;s wallet. Idempotent — safe to
            spam.
          </p>
        </div>
        <button
          type="button"
          onClick={run}
          disabled={busy}
          data-testid="system-payout-audit-run-btn"
          className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 border border-sky-500 text-sky-300 hover:bg-sky-500 hover:text-zinc-950 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-[10px] uppercase tracking-[0.3em]"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Stethoscope className="h-3.5 w-3.5" />}
          {busy ? "Running…" : "Run system check"}
        </button>
      </div>

      {result && s && (
        <div className="mt-4 space-y-3" data-testid="system-payout-audit-result">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
            <div className="border border-zinc-800 bg-zinc-950 p-2">
              <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">Stuck → minted</div>
              <div className="text-zinc-100 mt-1 font-mono">{s.stuck_triangulating ?? 0}</div>
            </div>
            <div className="border border-zinc-800 bg-zinc-950 p-2">
              <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">Verified re-minted</div>
              <div className="text-zinc-100 mt-1 font-mono">{s.verified_unminted ?? 0}</div>
            </div>
            <div className="border border-zinc-800 bg-zinc-950 p-2">
              <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">Citation coins</div>
              <div className="text-zinc-100 mt-1 font-mono">{s.citations_confirmed_unminted ?? 0}</div>
            </div>
            <div className="border border-emerald-500/30 bg-emerald-500/5 p-2">
              <div className="text-[9px] uppercase tracking-[0.25em] text-emerald-300">AEGIS paid</div>
              <div className="text-emerald-200 mt-1 font-mono">
                +{(s.newly_minted_apt ?? 0).toFixed(2)} APT
              </div>
            </div>
          </div>

          {(result.per_user || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
                Paid out · per profile
              </div>
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {result.per_user.map((u) => (
                  <div
                    key={u.user_id}
                    data-testid={`system-payout-row-${u.user_id}`}
                    className="flex items-center justify-between gap-3 border border-zinc-800 bg-zinc-950 p-2 text-[11px]"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <AtSign className="h-3 w-3 text-sky-400 shrink-0" />
                      <span className="font-mono text-zinc-200 truncate">{u.user_id}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-zinc-500 normal-case tracking-normal">
                        {u.actions.length} action{u.actions.length === 1 ? "" : "s"}
                      </span>
                      <span className="font-mono text-emerald-300">
                        +{(u.minted_apt ?? 0).toFixed(2)} APT
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {s.errors > 0 && (
            <div className="text-[11px] text-rose-300 font-mono">
              · {s.errors} error{s.errors === 1 ? "" : "s"} (see backend logs)
            </div>
          )}
        </div>
      )}
    </section>
  );
}



function ActiveUsersLedgerCard() {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [backfilling, setBackfilling] = useState(false);

  const load = React.useCallback(async (query = "") => {
    setLoading(true);
    try {
      const res = await architectUsersLedger({ q: query, limit: 500 });
      setUsers(res.users || []);
      setTotal(res.count ?? 0);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to load user ledger");
    } finally {
      setLoading(false);
    }
  }, []);

  const backfillWelcome = async () => {
    if (backfilling) return;
    setBackfilling(true);
    toast.info("Backfilling welcome AEGIS to every user who never got one…");
    try {
      const res = await architectBackfillWelcomeMints();
      toast.success(
        `Backfill complete · ${res.minted ?? 0} new welcome coin${res.minted === 1 ? "" : "s"} minted · ${res.skipped_already_had ?? 0} already had one.`
      );
      load(q);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  useEffect(() => { load(""); }, [load]);

  // Debounce search input.
  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
  }, [q, load]);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" }); }
    catch { return "—"; }
  };

  return (
    <section
      className="border border-violet-500/30 bg-violet-500/5 p-5"
      data-testid="active-users-ledger-card"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <Users className="h-4 w-4 text-violet-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-violet-300">
            Active users ledger
          </div>
          <div className="font-heading text-lg text-zinc-100 mt-1">
            Every signed-in profile
          </div>
          <p className="text-xs text-zinc-400 mt-2 leading-relaxed max-w-2xl">
            The complete list of non-anonymous users — even the ones who
            never claimed a @handle and would otherwise be hidden from
            search. Shows AEGIS holdings, submission count, last-seen,
            and onboarding flags so you can spot who needs nudging.
          </p>
        </div>
        <button
          type="button"
          onClick={() => load(q)}
          disabled={loading}
          data-testid="active-users-ledger-refresh-btn"
          className="shrink-0 inline-flex items-center gap-2 px-3 py-2 border border-violet-500/40 text-violet-300 hover:border-violet-400 disabled:opacity-50 transition-colors text-[10px] uppercase tracking-[0.3em]"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Refresh
        </button>
        <button
          type="button"
          onClick={backfillWelcome}
          disabled={backfilling}
          data-testid="active-users-backfill-welcome-btn"
          title="Mint the welcome AEGIS coin to every existing user who never got one"
          className="shrink-0 inline-flex items-center gap-2 px-3 py-2 border border-emerald-500/40 text-emerald-300 hover:border-emerald-400 disabled:opacity-50 transition-colors text-[10px] uppercase tracking-[0.3em]"
        >
          {backfilling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Gift className="h-3.5 w-3.5" />}
          {backfilling ? "Minting…" : "Backfill welcome"}
        </button>
      </div>

      <div className="mt-4 relative">
        <Search className="h-3.5 w-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="search handle, email, display name, user_id…"
          data-testid="active-users-ledger-search"
          className="w-full bg-zinc-950 border border-zinc-800 focus:border-violet-500/60 outline-none text-zinc-100 px-9 py-2 text-sm"
        />
      </div>

      <div
        className="mt-3 text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono"
        data-testid="active-users-ledger-count"
      >
        {loading ? "loading…" : `${total} user${total === 1 ? "" : "s"}`}
      </div>

      <div className="mt-3 overflow-x-auto" data-testid="active-users-ledger-table">
        <table className="w-full text-left text-[11px] font-mono border-collapse">
          <thead className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">
            <tr>
              <th className="py-1.5 pr-3">Handle / Display</th>
              <th className="py-1.5 pr-3">Email</th>
              <th className="py-1.5 pr-3">Joined</th>
              <th className="py-1.5 pr-3">Last seen</th>
              <th className="py-1.5 pr-3">AEGIS · APT</th>
              <th className="py-1.5 pr-3">Subs</th>
              <th className="py-1.5">Flags</th>
            </tr>
          </thead>
          <tbody className="text-zinc-300">
            {users.map((u) => (
              <tr
                key={u.user_id}
                data-testid={`active-users-row-${u.user_id}`}
                className="border-t border-zinc-900 hover:bg-zinc-900/40 transition-colors"
              >
                <td className="py-2 pr-3 max-w-[200px] truncate">
                  <div className="flex items-center gap-1.5">
                    <AtSign className="h-3 w-3 text-violet-400 shrink-0" />
                    <span className={u.handle ? "text-zinc-100" : "text-zinc-500 italic"}>
                      {u.handle || "(no handle)"}
                    </span>
                  </div>
                  {u.display_name && (
                    <div className="text-zinc-500 normal-case text-[10px] truncate">
                      {u.display_name}
                    </div>
                  )}
                </td>
                <td className="py-2 pr-3 text-zinc-400 normal-case text-[10px] max-w-[180px] truncate" title={u.email}>
                  {u.email || "—"}
                </td>
                <td className="py-2 pr-3 text-zinc-500 normal-case">{fmtDate(u.created_at)}</td>
                <td className="py-2 pr-3 text-zinc-500 normal-case">{fmtDate(u.last_seen)}</td>
                <td className={`py-2 pr-3 ${u.aegis_apt > 0 ? "text-emerald-300" : "text-zinc-600"}`}>
                  {u.aegis_apt.toFixed(2)}
                  <span className="text-zinc-700"> · </span>
                  <span className="text-zinc-600">{u.aegis_coin_count}c</span>
                </td>
                <td className={`py-2 pr-3 ${u.submissions > 0 ? "text-sky-300" : "text-zinc-600"}`}>
                  {u.submissions}
                </td>
                <td className="py-2">
                  <div className="flex flex-wrap gap-1 normal-case text-[9px] uppercase tracking-[0.15em]">
                    {u.is_architect && (
                      <span className="px-1.5 py-0.5 border border-amber-500/40 text-amber-300">arch</span>
                    )}
                    {!u.username_set && (
                      <span className="px-1.5 py-0.5 border border-rose-500/40 text-rose-300" title="No @handle claimed — invisible to public search">
                        no-handle
                      </span>
                    )}
                    {!u.password_set && (
                      <span className="px-1.5 py-0.5 border border-zinc-700 text-zinc-500">no-pw</span>
                    )}
                    {u.has_solana_wallet && (
                      <span className="px-1.5 py-0.5 border border-emerald-500/40 text-emerald-300">wallet</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!loading && users.length === 0 && (
              <tr>
                <td colSpan={7} className="py-6 text-center text-zinc-500 normal-case">
                  No users match &ldquo;{q}&rdquo;.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

